import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Modal } from 'antd';
import PropTypes from 'prop-types';
import Input from '../../../../components/elements/input/Input';
import { createSender, updateSender } from '../../api/sendersApi';

const emptyForm = {
  fullName: '',
  street: '',
  postalCode: '',
  city: '',
  phone: '',
  bankAccount: '',
};

const SenderModal = ({ isOpen, onClose, onSaved, sender }) => {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const isEdit = Boolean(sender?._id);

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      fullName: sender?.fullName || '',
      street: sender?.street || '',
      postalCode: sender?.postalCode || '',
      city: sender?.city || '',
      phone: sender?.phone || '',
      bankAccount: sender?.bankAccount || '',
    });
    setLoading(false);
    setDone(false);
    setError('');
    setSuccessMessage('');
  }, [isOpen, sender]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleOk = useCallback(async () => {
    const fullName = form.fullName.trim();
    const street = form.street.trim();
    const postalCode = form.postalCode.trim();
    const city = form.city.trim();
    const phone = form.phone.trim();

    if (!fullName || !street || !postalCode || !city || !phone) {
      setError('Sva polja osim ziro racuna su obavezna.');
      return;
    }

    // Ziro racun je opcion; prazna vrednost ('') na PUT-u brise postojeci
    // podatak na backendu, a na POST-u je dozvoljena (polje nije obavezno).
    const payload = {
      fullName,
      street,
      postalCode,
      city,
      phone,
      bankAccount: form.bankAccount.trim(),
    };

    setLoading(true);
    setError('');
    setSuccessMessage('');

    const response = isEdit
      ? await updateSender(sender._id, payload)
      : await createSender(payload);

    if (!response.ok) {
      setError(response.message);
      setLoading(false);
      return;
    }

    setSuccessMessage(
      response.message ||
        (isEdit ? 'Posiljalac azuriran.' : 'Posiljalac kreiran.')
    );
    setDone(true);
    onSaved?.();

    setTimeout(() => {
      setDone(false);
      onClose();
    }, 900);

    setLoading(false);
  }, [form, isEdit, sender?._id, onClose, onSaved]);

  const handleCancel = () => {
    setForm(emptyForm);
    setLoading(false);
    setDone(false);
    setError('');
    setSuccessMessage('');
    onClose();
  };

  return (
    <Modal
      className="create_user_modal"
      title={isEdit ? 'Izmeni posiljaoca' : 'Dodaj posiljaoca'}
      open={isOpen}
      onCancel={handleCancel}
      footer={[
        <Button
          key="save"
          type="primary"
          onClick={handleOk}
          disabled={loading || done}
          style={{ width: '190px' }}
        >
          {loading ? (
            <img
              src="/assets/icons/add.svg"
              alt="Učitavanje"
              className="animate-spin"
            />
          ) : done ? (
            <img
              src="/assets/icons/check.svg"
              alt="Done"
              className="check-icon"
            />
          ) : isEdit ? (
            'Izmeni posiljaoca'
          ) : (
            'Dodaj posiljaoca'
          )}
        </Button>,
      ]}
    >
      <div className="create_user_modal_form" autoComplete="off">
        <Input
          required
          label="Ime i prezime"
          id="senderFullName"
          name="fullName"
          placeholder="Ime i prezime"
          value={form.fullName}
          onChange={handleChange}
        />
        <Input
          required
          label="Ulica"
          id="senderStreet"
          name="street"
          placeholder="Ulica"
          value={form.street}
          onChange={handleChange}
        />
        <Input
          required
          label="Poštanski broj"
          id="senderPostalCode"
          name="postalCode"
          placeholder="Poštanski broj"
          value={form.postalCode}
          onChange={handleChange}
        />
        <Input
          required
          label="Grad"
          id="senderCity"
          name="city"
          placeholder="Grad"
          value={form.city}
          onChange={handleChange}
        />
        <Input
          required
          label="Kontakt (broj telefona)"
          id="senderPhone"
          name="phone"
          placeholder="Kontakt (broj telefona)"
          value={form.phone}
          onChange={handleChange}
        />
        <Input
          label="Žiro račun (opciono)"
          id="senderBankAccount"
          name="bankAccount"
          placeholder="Žiro račun"
          value={form.bankAccount}
          onChange={handleChange}
        />
        {successMessage && (
          <Alert
            title={successMessage}
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        {error && (
          <Alert
            title={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
      </div>
    </Modal>
  );
};

SenderModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func,
  sender: PropTypes.shape({
    _id: PropTypes.string,
    fullName: PropTypes.string,
    street: PropTypes.string,
    postalCode: PropTypes.string,
    city: PropTypes.string,
    phone: PropTypes.string,
    bankAccount: PropTypes.string,
  }),
};

export default SenderModal;
